<script lang="ts" setup>
import { ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import RoamingSearch from "@/components/roaming/RoamingSearch.vue";
import RoamingInfoDetails from "@/components/roaming/RoamingInfoDetails.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import { useI18n } from "vue-i18n";
import { useRoamingView } from "@/composables/roaming/useRoamingView";

const { t } = useI18n();
const roamingInfoTitle = ref<HTMLElement | null>(null);

const {
  identicalCountriesWithActive,
  isSticky,
  othersCountriesList,
  othersCountriesCount,
  popularCountries,
  selectedCountry,
  selectedZone,
  selectCountry,
} = useRoamingView(roamingInfoTitle);
</script>

<template>
  <div class="roaming">
    <div class="roaming-wrapper">
      <div class="roaming-header">
        <router-link to="account" class="roaming-header__back">
          <BaseFigureForIcon
            size="medium"
            color="white"
            icon="arrow"
            position="left"
            is-button
          />
        </router-link>
        <div class="roaming-header__title" :class="isSticky ? 'visible' : ''">
          <p class="h3">{{ t("roaming.page.title") }}</p>
        </div>
      </div>
      <main class="roaming-main">
        <div class="roaming-main__wrapper">
          <div class="roaming-main__header">
            <div class="roaming-main__header-title">
              <p class="h1">{{ t("roaming.page.selectCountry") }}</p>
            </div>
          </div>

          <div class="roaming-banner">
            <div class="roaming-banner__wrapper">
              <div class="roaming-banner__content">
                <div class="roaming-banner__countries">
                  <RoamingSearch
                    class="roaming-banner__countries-search"
                    v-model="selectedCountry"
                  />
                  <swiper
                    v-if="popularCountries"
                    :space-between="8"
                    :slides-per-view="'auto'"
                    class="roaming-banner__countries-popular"
                  >
                    <swiper-slide
                      v-for="(item, index) in popularCountries"
                      :key="index"
                      @click="selectCountry(item)"
                      class="roaming-banner__countries-popular__item"
                      :class="{
                        '--active': identicalCountriesWithActive(item.uuid),
                      }"
                    >
                      <span class="footnote color-text-primary uppercase">
                        {{ item.country_name_ru }}
                      </span>
                    </swiper-slide>
                  </swiper>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="selectedCountry"
            class="roaming-info"
            ref="roamingInfoTitle"
          >
            <div class="roaming-info__row">
              <RoamingInfoDetails
                :country="selectedCountry"
                :zone="selectedZone"
                :othersCountriesList="othersCountriesList"
                :othersCountriesCount="othersCountriesCount"
                @select="selectCountry"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

.roaming {
  position: relative;
  width: 100%;
  margin-top: 44px;
  margin-bottom: 44px;
  max-width: 720px;
  flex: 1;
  min-height: calc(var(--app-height) - 44px * 2);
  @media (max-width: 960px) {
    width: 100%;
    margin-bottom: 118px;
    margin-top: 24px;
    max-width: 100%;
    padding: 0 24px;
  }
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 118px;
    margin-top: 24px;
    max-width: 100%;
    padding: 0;
  }

  &-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: calc(var(--app-height) - 44px * 2);
  }

  &-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    margin-bottom: 31px;
    @media (max-width: 768px) {
      grid-template-columns: auto 1fr auto;
      position: sticky;
      top: 0;
      padding: 13px;
      z-index: 1;
    }

    &__back {
      width: min-content;
    }

    &__title {
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 768px) {
        opacity: 0;
        transition: opacity 0.2s;

        &.visible {
          opacity: 1;
        }
      }
    }
  }

  &-main {
    width: 100%;
    background: var(--color-white);
    border-radius: 32px;
    position: relative;
    flex: 1;
    @media (max-width: 768px) {
      background: transparent;
    }

    &__wrapper {
      padding: 25px 35px 40px 24px;
      @media (max-width: 768px) {
        padding: 0 24px;
      }
    }

    &__header {
      display: flex;
      flex-flow: column;
      gap: 5px;
      max-width: 388px;
    }
  }

  &-banner {
    position: relative;
    height: 100%;
    width: 100%;

    &__wrapper {
      width: 100%;
    }

    &__content {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      flex-flow: column;
      z-index: 1;
      margin-top: 26px;
    }

    &__title {
      margin-bottom: 25px;
      font-size: 48px;
      line-height: 120%;
      @media (max-width: 1600px) {
        font-size: 40px;
      }
      @media (max-width: 768px) {
        margin-bottom: 16px;
        font-size: 28px;
      }
    }

    &__subtitle {
      margin-bottom: 80px;
      max-width: 550px;
      @media (max-width: 1600px) {
        margin-bottom: 54px;
      }
      @media (max-width: 768px) {
        margin-bottom: 64px;
      }

      .caption-medium {
        font-weight: 400;
        font-size: 16px;
      }
    }

    &__countries {
      display: flex;
      flex-direction: column;
      gap: 16px;

      &-popular {
        width: 100%;
        display: flex;

        &__item {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 12px 6px;
          background-color: transparent;
          border: 1px solid var(--color-grey-blue);
          border-radius: 12px;
          cursor: pointer;
          flex-shrink: 0;
          min-width: 70px;
          width: auto;
          transition: background 0.2s;

          &.--active {
            background: var(--color-secondary);
            span {
              color: var(--color-white);
            }
          }

          span {
            transition: color 0.2s;
            text-wrap: nowrap;

            &.footnote {
              font-weight: 700;
              @media (max-width: 768px) {
                font-size: 10px;
              }
            }
          }
        }
      }
    }
  }

  &-info {
    position: relative;
    display: flex;
    width: 100%;
    margin-top: 24px;
    scroll-margin: 20px;
    @media (max-width: 768px) {
      margin-top: 32px;
    }

    &__row {
      width: 100%;
    }
  }
}
</style>
